import axios, { CancelToken } from "axios";
import authStore from "../stores/AuthStore";

export class ApiHandler {
  lastApiHost = "";

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_BACKEND_URL,
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });

    this.api.interceptors.request.use((config) => {
      const currentUser = authStore.getUser();
      const userID = currentUser?.id || currentUser?.userID;
      // console.log("User ID in apiHandler.js: ", userID);
      if (userID) {
        config.headers["X-User-ID"] = userID;
        if (config.method !== "get") {
          config.data = { userID, ...config.data };
        }
      }
      return config;
    });
  }

  async getHeaders() {
    return { "Content-Type": "application/json" };
  }

  handleErrorCode(error) {
    if (error.response) {
      const { status, data } = error.response;
      const message = data?.message || "An unexpected error occurred.";
      console.error(`API Error (${status}): ${message}`, data);
      // throw new Error(message);
      throw error;
    }
    console.error("API Request Error:", error.message);
    throw new Error(error.message);
  }

  async request(method, endpoint, data) {
    try {
      const headers = await this.getHeaders();
      const cancelToken = CancelToken.source();

      const response = await this.api.request({
        method,
        url: endpoint,
        data,
        headers,
        cancelToken: cancelToken.token,
      });

      this.updateLastApiHost(this.api.defaults.baseURL + endpoint);
      return response.data;
    } catch (error) {
      this.handleErrorCode(error);
    }
  }

  updateLastApiHost(url) {
    try {
      this.lastApiHost = new URL(url).host;
    } catch (error) {
      console.error("Error parsing URL:", error);
    }
  }

  getLastApiHost() {
    return this.lastApiHost || "No API request made yet";
  }

  get(endpoint) {
    return this.request("get", endpoint);
  }

  post(endpoint, data) {
    return this.request("post", endpoint, data);
  }

  put(endpoint, data) {
    return this.request("put", endpoint, data);
  }

  delete(endpoint, data) {
    return this.request("delete", endpoint, data);
  }

  patch(endpoint, data) {
    return this.request("patch", endpoint, data);
  }

  async pingDevice(deviceUrl) {
    try {
      const start = performance.now();
      await axios.get(`http://${deviceUrl}`, { timeout: 5000 });
      const end = performance.now();
      return `Ping successful. Response time: ${end - start} ms`;
    } catch (error) {
      if (error.code === "ECONNABORTED")
        return "Ping timed out. Unit might be offline.";
      return "Ping failed. Unit is likely offline.";
    }
  }

  async getCurrentIP() {
    const response = await axios.get("https://api.ipify.org?format=json");
    return response.data.ip;
  }

  async getApiIP() {
    return Promise.resolve(this.getLastApiHost());
  }
}

const apiHandler = new ApiHandler();
export default apiHandler;
